/**
 * Created by Ning Jiang on 2021-10-04.
 */

import {WinPresentationController} from "../WinPresentationController";
import {TombstoneRip} from "../../TombstoneRip";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {TombstoneRipSymbolState} from "../../symbol/TombstoneRipSymbolConfig";
import {gsap, TimelineLite} from "gsap";
import {IReelStateEventData, ReelState} from "../../../core/reel/ReelState";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {MathHelper} from "../../../core/utils/MathHelper";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {TombstoneRipGameMode} from "../../gameflow/TombstoneRipGameMode";
import {TombstoneRipSound} from "../../sound/TombstoneRipSound";
import {TombstoneRipBackground} from "../../view/TombstoneRipBackground";

export class UpgradeMController extends WinPresentationController {

    private _respinTime:number = 2;
    private _respinQuickTime:number = 1;

    private _isRespinning:boolean = false;

    constructor() {
        super("UpgradeMController", false);
    }

    protected hasWin():boolean {
        const data = TombstoneRip.model.parsedGameData;
        if(data.mode !== GameMode.NORMAL) {
            return false;
        }
        if(data.freespinTriggeredThisSpin) {
            return false;
        }
        return data.reels[4][1] === "S2";
    }

    protected async createWinAnimation():Promise<any> {
        const data = TombstoneRip.model.parsedGameData;
        if(data.superFSInfo == null) {
            debugger;
            throw new Error("superFSInfo missing for upgrading S2!");
        }
        if(!data.superFSInfo.respinReel5) {
            TombstoneRip.reel5Frame.startSuperFs(GameMode.NORMAL, "broken");
            TombstoneRip.reels[4].replaceSimpleSymbol(0, `${data.superFSInfo.outlawWild}D`, TombstoneRipSymbolState.NO_WIN);
            TombstoneRip.background.currentMode = "NORMAL_SUPER_NO_WIN";
            return new Promise<any>((resolve, reject) => {
                this.onRespinStopped(resolve);
            });
        }

        TombstoneRip.background.updateEnvironment(TombstoneRipGameMode.NORMAL_SUPER);
        TombstoneRipSound.soundPlayer.playTrack({name : TombstoneRipSound.RESPIN_LOOP, volume : 1, repeat : 100});
        await new Promise<any>((resolve, reject) => {
            this.respin(resolve);
        });
    }

    private respin(resolve:(value:any) => void):void {
        const quickSpin = SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN) || TombstoneRip.reels[4].isQuickStop;
        const tl = new TimelineLite();
        tl.add(() => TombstoneRip.reel5Frame.startSuperSpin());
        tl.add(TombstoneRip.reels[4].startSpin(true, true));
        tl.add(() => {
            TombstoneRip.reels[4].events.once(ReelState.STOPPED, async (data:IReelStateEventData) => {
                TombstoneRipSound.fadeAndStopSound({
                    name : TombstoneRipSound.RESPIN_SPIN,
                    to : 0,
                    duration : 100
                });
                TombstoneRipSound.soundPlayer.playTrack({name : TombstoneRipSound.SUPER_FS_REELS_LANDING[4]})
                TombstoneRipBackground.shakeStageOnce(1.2);
                this.onRespinStopped(resolve);
            });
            TombstoneRip.reels[4].tryStop(this.getRespinStopReels(), 0, quickSpin ? this._respinQuickTime : this._respinTime, false, () => {}, undefined);
            this._isRespinning = true;
            this._flowStateController.setSkipCallback(() => this.skip!());
        })

        if(tl.duration() <= 0) {
            tl.add(gsap.to(this, {duration : 0.001}));
        }
    }

    private getRespinStopReels():string[] {
        return ArrayHelper.initArrayWithValues(5, index => {
            if(index === 1) {
                return `${TombstoneRip.model.parsedGameData.superFSInfo.outlawWild}D`;
            }

            return `M${MathHelper.randomNumberInRange(1, 5)}D`;
        });
    }

    private onRespinStopped(resolve:(value:any) => void):void {
        this._isRespinning = false;

        const tl = new TimelineLite();
        let hasWild = false;
        // add wild patch to all wild M symbols.
        TombstoneRip.reels.forEach((reel, reelIndex) => {
            reel.superWildM = TombstoneRip.model.parsedGameData.superFSInfo.outlawWild;
            if(reelIndex === 4) {
                return;
            }

            const symbolIds = reelIndex === 0 ? [0, 1] : [0, 1, 2];
            symbolIds.forEach(symId => {
                const symbol = reel.getSymbol(symId);
                if(symbol.symbolName === TombstoneRip.model.parsedGameData.superFSInfo.outlawWild) {
                    reel.replaceSimpleSymbol(symId, symbol.symbolName);
                    const newSymbol = reel.getSymbol(symId);
                    tl.add(newSymbol.setWild(true), 0);
                    hasWild = true;
                }
            });
        });
        if(hasWild) {
            tl.add(() => {TombstoneRipSound.soundPlayer.playTrack({name : TombstoneRipSound.SYMBOL_TO_WILD})}, 0);
        }
        tl.add(() => TombstoneRip.reel5Frame.stopSuperSpin(), 0);

        const endCallback = async () => {
            if(!TombstoneRip.model.parsedGameData.superFSInfo.multiplierReveal) {
                await TombstoneRip.superFsPop.show(false, false, false);
                this.revealMultiplierM(resolve, false);
            } else {
                this._flowStateController.setSkipCallback(() => this.skip!());
                await TombstoneRip.superFsPop.show(true, this.useMultCountUp(), true);
                this.revealMultiplierM(resolve, true);
            }
        };

        tl.add(() => {
            endCallback();
        });
    }

    private useMultCountUp():boolean {
        const data = TombstoneRip.model.parsedGameData;
        if(data.superFSInfo.outlawMultiplier === data.superFSInfo.outlawWild) {
            return true;
        }

        return data.betWins.some(betWin => betWin.symbol === data.superFSInfo.outlawMultiplier);

        // for(let i = 0; i < data.betWins.length; i++) {
        //     if(data.betWins[i].symbol === data.superFSInfo.outlawMultiplier) {
        //         return true;
        //     }
        // }
        //
        // return false;
    }

    private revealMultiplierM(resolve:(value:any) => void, playFireIfMatch:boolean):void {
        this._flowStateController.setSkipCallback();
        const tl = gsap.timeline();
        tl.add(() => {
            TombstoneRip.reels.forEach((reel, reelIndex) => {
                reel.superMultiplierM = TombstoneRip.model.parsedGameData.superFSInfo.outlawMultiplier;
                if(reelIndex === 4) {
                    const symbol = reel.getSymbol(0);
                    if(symbol.symbolName.includes(TombstoneRip.model.parsedGameData.superFSInfo.outlawMultiplier)) {
                        reel.replaceSimpleSymbol(0, symbol.symbolName);
                        const newSymbol = reel.getSymbol(0);
                        newSymbol.setMultiplier(TombstoneRip.model.parsedGameData.superFSInfo.outlawMultiplierValue, false);
                    }
                    return;
                }

                const symbolIds = reelIndex === 0 ? [0, 1] : [0, 1, 2];
                symbolIds.forEach(symId => {
                    const symbol = reel.getSymbol(symId);
                    if(symbol.symbolName === TombstoneRip.model.parsedGameData.superFSInfo.outlawMultiplier) {
                        reel.replaceSimpleSymbol(symId, symbol.symbolName);
                        const newSymbol = reel.getSymbol(symId);
                        let match = false;
                        if(newSymbol.symbolName === TombstoneRip.model.parsedGameData.superFSInfo.outlawWild) {
                            match = true;
                            if(!playFireIfMatch) {
                                newSymbol.resetFire();
                            }
                            newSymbol.setWild(true);
                        }
                        newSymbol.setMultiplier(TombstoneRip.model.parsedGameData.superFSInfo.outlawMultiplierValue, match && playFireIfMatch);
                    }
                });
            });

        }, 0.5);

        tl.add(() => TombstoneRip.superFsPop.moveToSide());
        tl.add(() => resolve(undefined), `+=0.6`);
    }

    protected skip() {
        if(this._isRespinning) {
            TombstoneRip.reels[4].tryStop(this.getRespinStopReels(), 0, 0, true, () => {}, undefined);
            return;
        }
        if(TombstoneRip.superFsPop.skip()) {
            this._flowStateController.setSkipCallback(() => this.skip());
        }
    }
}